import {
  Button,
  DocViewerComponent,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { documentName } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { useServiceRequestFile } from '../hook/useServiceRequestFile';
import { CorporationUploadFileModal } from './modals/CorporationUploadFileModal';
import DirectorUploadFileModal from './modals/DirectorUploadFileModal';

const DocumentPreview = ({
  documentType,
  directorId,
  onUploadSuccess,
  viewOnly = false,
  serviceRequest,
  mutate,
  serviceRequestTaskId,
}: {
  documentType: LegacyDocumentType;
  directorId?: number;
  onUploadSuccess?: () => void;
  viewOnly?: boolean;
  serviceRequest: ServiceRequestDetails;
  mutate: KeyedMutator<unknown>;
  serviceRequestTaskId?: number;
}) => {
  const [file, setFile] = useServiceRequestFile(serviceRequest, documentType);
  const [documentLink, setDocumentLink] = useState('');
  const uploadModalVisibility = useToggle();

  const uploadedDocument = useMemo(() => {
    if (directorId) {
      const currentDirector =
        serviceRequest?.corporation?.corporationHasDirector?.find(
          (each) => each?.director?.id === directorId
        );
      return currentDirector?.documents?.find(
        (each) => each?.documentType === documentType
      );
    }
    return serviceRequest?.corporation?.corporationDocuments?.find(
      (each) => each?.documentType === documentType
    );
  }, [serviceRequest, documentType, directorId]);

  useEffect(() => {
    let mounted = true;

    const fetchSignedUrl = async () => {
      try {
        if (uploadedDocument?.files?.[0]?.id) {
          const link = await getSignedUrlByFileId(
            uploadedDocument.files[0].id,
            null
          );
          if (mounted) {
            setDocumentLink(link);
          }
        }
      } catch (error) {
        console.error('Error fetching signed URL:', error);
      }
    };

    fetchSignedUrl();

    return () => {
      mounted = false;
    };
  }, [serviceRequest, uploadedDocument]);

  return (
    <div className="flex flex-col h-full">
      {directorId ? (
        <DirectorUploadFileModal
          serviceRequestTaskId={serviceRequestTaskId}
          serviceRequest={serviceRequest}
          file={file}
          directorId={directorId}
          setFile={setFile}
          isOpen={uploadModalVisibility.visible}
          setIsOpen={uploadModalVisibility.hide}
          onUploadSuccess={async () => {
            if (onUploadSuccess) onUploadSuccess();
            await mutate();
            setFile(null);
          }}
          documentType={documentType}
          heading={`Upload Signed ${documentName(documentType)}`}
        />
      ) : (
        <CorporationUploadFileModal
          serviceRequestTaskId={serviceRequestTaskId}
          serviceRequest={serviceRequest}
          file={file}
          setFile={setFile}
          isOpen={uploadModalVisibility.visible}
          setIsOpen={uploadModalVisibility.hide}
          onUploadSuccess={async () => {
            if (onUploadSuccess) onUploadSuccess();
            await mutate();
            setFile(null);
          }}
          documentType={documentType}
          heading={`Upload Signed ${documentName(documentType)}`}
        />
      )}
      <div className="flex items-center justify-between mb-4">
        <h1 className="txt-extra-large text-text-primary">
          {documentName(documentType)}
        </h1>
        {!viewOnly ? (
          <Button
            variant={uploadedDocument ? 'secondary' : 'primary'}
            onClick={uploadModalVisibility.toggle}
            size="sm"
          >
            <Icons.Upload01 className="w-4 h-4" />
            {uploadedDocument ? 'Reupload' : 'Upload'}
          </Button>
        ) : null}
      </div>
      {uploadedDocument ? (
        <DocViewerComponent uri={documentLink} />
      ) : (
        <div className="rounded-2xl bg-bg-secondary h-full mt-6 flex flex-col items-center justify-center">
          <div className="flex h-12 w-12 rounded-lg bg-white border border-primary items-center justify-center mb-5">
            <Icons.File05 className="h-5 w-5" />
          </div>
          <div className="mt-4 flex flex-col gap-1">
            <h3 className="txt-medium-bold text-center text-text-primary">
              No document uploaded
            </h3>
            <p className="text-center">
              Once a document has been uploaded, a preview will load here.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPreview;
